import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useStableAuth } from './useStableAuth';
import { toast } from 'sonner';
import type { TaskCollaborator, CollaboratorRole } from '@/types/task';

export const useTaskCollaborators = (taskId?: string) => {
  const { userId, companyId } = useStableAuth();
  const [collaborators, setCollaborators] = useState<TaskCollaborator[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchCollaborators = async () => {
    if (!taskId || !userId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('task_collaborators')
        .select('*')
        .eq('task_id', taskId)
        .order('added_at', { ascending: true });

      if (error) throw error;
      
      // Fetch user profiles separately
      const userIds = data?.map(c => c.user_id) || [];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, full_name, avatar_url')
        .in('user_id', userIds);

      const formattedData = data?.map(collab => ({
        ...collab,
        role: collab.role as CollaboratorRole,
        user: profiles?.find(p => p.user_id === collab.user_id) ? {
          id: profiles.find(p => p.user_id === collab.user_id)!.user_id,
          full_name: profiles.find(p => p.user_id === collab.user_id)!.full_name,
          avatar_url: profiles.find(p => p.user_id === collab.user_id)?.avatar_url
        } : undefined
      })) || [];
      
      setCollaborators(formattedData);
    } catch (error) {
      console.error('Erro ao carregar colaboradores:', error);
      toast.error('Erro ao carregar colaboradores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollaborators();
  }, [taskId, userId]);

  const addCollaborator = async (newUserId: string, role: CollaboratorRole = 'collaborator') => {
    if (!taskId || !userId) return;

    try {
      const { error } = await supabase
        .from('task_collaborators')
        .insert({
          task_id: taskId,
          user_id: newUserId,
          role,
          added_by: userId,
          company_id: companyId!
        });

      if (error) throw error;
      
      toast.success('Colaborador adicionado com sucesso');
      await fetchCollaborators();
    } catch (error) {
      console.error('Erro ao adicionar colaborador:', error);
      toast.error('Erro ao adicionar colaborador');
      throw error;
    }
  };

  const removeCollaborator = async (collaboratorId: string) => {
    try {
      const { error } = await supabase
        .from('task_collaborators')
        .delete()
        .eq('id', collaboratorId);

      if (error) throw error;
      
      toast.success('Colaborador removido com sucesso');
      await fetchCollaborators();
    } catch (error) {
      console.error('Erro ao remover colaborador:', error);
      toast.error('Erro ao remover colaborador');
      throw error;
    }
  };

  const updateCollaboratorRole = async (collaboratorId: string, newRole: CollaboratorRole) => {
    try {
      const { error } = await supabase
        .from('task_collaborators')
        .update({ role: newRole })
        .eq('id', collaboratorId);

      if (error) throw error;
      
      toast.success('Papel atualizado com sucesso');
      await fetchCollaborators();
    } catch (error) {
      console.error('Erro ao atualizar papel:', error);
      toast.error('Erro ao atualizar papel');
      throw error;
    }
  };

  return {
    collaborators,
    loading,
    addCollaborator,
    removeCollaborator,
    updateCollaboratorRole,
    refetch: fetchCollaborators
  };
};
